import axios from 'axios';
import {
  GET_SUBMISSIONS,
  GET_SUBMISSION,
  ADD_SUBMISSION,
  UPDATE_SUBMISSION,
  DELETE_SUBMISSION,
  REVIEW_SUBMISSION,
  SUBMISSIONS_LOADING,
  GET_SUBMISSION_STATS,
  GET_USER_SUBMISSION_STATUS,
  SET_SUBMISSIONS_PAGE,
  CLEAR_CURRENT_SUBMISSION
} from './types';
import { tokenConfig, loadUser } from './authActions';
import { returnErrors } from './errorActions';

// Get all submissions with pagination and filters
export const getSubmissions = (page = 1, limit = 10, filters = {}) => (dispatch, getState) => {
  dispatch(setSubmissionsLoading());

  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);

  Object.keys(filters).forEach(key => {
    if (filters[key] && filters[key] !== 'all') {
      if (Array.isArray(filters[key])) {
        // For array filters like status or formId
        filters[key].forEach(value => params.append(key, value));
      } else {
        params.append(key, filters[key]);
      }
    }
  });

  const url = `/api/submissions?${params.toString()}`;

  axios
    .get(url, tokenConfig(getState))
    .then(res => dispatch({
      type: GET_SUBMISSIONS,
      payload: res.data
    }))
    .catch(err => dispatch(returnErrors(err.response.data, err.response.status)));
};

// Get specific submission
export const getSubmission = (id) => (dispatch, getState) => {
  dispatch(setSubmissionsLoading());
  
  axios
    .get(`/api/submissions/${id}`, tokenConfig(getState))
    .then(res => dispatch({
      type: GET_SUBMISSION,
      payload: res.data
    }))
    .catch(err => dispatch(returnErrors(err.response.data, err.response.status)));
};

// Create new submission
export const addSubmission = (submissionData) => (dispatch, getState) => {
  return axios
    .post('/api/submissions', submissionData, tokenConfig(getState))
    .then(res => {
      dispatch({
        type: ADD_SUBMISSION,
        payload: res.data
      });

      // Fetch updated submission status to ensure UI updates correctly
      const userId = getState().auth.user?.id || getState().auth.user?._id;
      const formId = submissionData.submissionFormId;
      if (userId && formId) {
        dispatch(getUserSubmissionStatus(userId, formId));
      }
    })
    .catch(err => dispatch(returnErrors(err.response.data, err.response.status)));
};

// Update submission
export const updateSubmission = (id, submissionData) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/submissions/${id}`, submissionData, tokenConfig(getState));

    dispatch({
      type: UPDATE_SUBMISSION,
      payload: res.data
    });

    // Fetch updated submission status to ensure UI updates correctly
    const userId = getState().auth.user?.id || getState().auth.user?._id;
    const formId = submissionData.submissionFormId;
    if (userId && formId) {
      dispatch(getUserSubmissionStatus(userId, formId));
    }

    return res.data;
  } catch (err) {
    dispatch(returnErrors(err.response.data, err.response.status));
    throw err;
  }
};

// Delete submission
export const deleteSubmission = (id) => (dispatch, getState) => {
  axios
    .delete(`/api/submissions/${id}`, tokenConfig(getState))
    .then(() => dispatch({
      type: DELETE_SUBMISSION,
      payload: id
    }))
    .catch(err => dispatch(returnErrors(err.response.data, err.response.status)));
};

// Review submission (instructor/admin)
export const reviewSubmission = (id, reviewData) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/submissions/${id}`, reviewData, tokenConfig(getState));

    dispatch({
      type: REVIEW_SUBMISSION,
      payload: res.data
    });

    return res.data;
  } catch (err) {
    dispatch(returnErrors(err.response.data, err.response.status));
    throw err;
  }
};

// Get submission statistics
export const getSubmissionStats = (filters = {}) => (dispatch, getState) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.append(key, filters[key]);
    }
  });
  
  const url = `/api/submissions/stats/overview${params.toString() ? `?${params.toString()}` : ''}`;
  
  axios
    .get(url, tokenConfig(getState))
    .then(res => dispatch({
      type: GET_SUBMISSION_STATS,
      payload: res.data
    }))
    .catch(err => dispatch(returnErrors(err.response.data, err.response.status)));
};

// Get user's submission status for a specific form
export const getUserSubmissionStatus = (userId, formId) => (dispatch, getState) => {
  axios
    .get(`/api/submissions/user/${userId}/form/${formId}`, tokenConfig(getState))
    .then(res => dispatch({
      type: GET_USER_SUBMISSION_STATUS,
      payload: { formId, ...res.data }
    }))
    .catch(err => dispatch(returnErrors(err.response?.data, err.response?.status)));
};

// Set loading state
export const setSubmissionsLoading = () => {
  return { type: SUBMISSIONS_LOADING };
};

// Set current page number (without fetching data)
export const setSubmissionsPage = (page) => {
  return { type: SET_SUBMISSIONS_PAGE, payload: page };
};

// Clear current submission
export const clearCurrentSubmission = () => {
  return { type: CLEAR_CURRENT_SUBMISSION };
}; 